import { useState } from 'react';
import { Button, Choice, Modal, Reason, useToast } from '@efira/ui';

import { useApplyPreset } from '../../../api/queries';
import type { BindableAction, Binding, BindingPreset, GiftsPayload } from '../../../api/queries';
import { EVENT_LABELS, coins, label } from '../../../bindings/labels';

/**
 * Готовые наборы привязок.
 *
 * ЗАЧЕМ ОНИ ЕСТЬ. Настроить десять действий руками долго; набор сворачивает
 * это в один выбор. У эталона наборов нет вовсе - есть только заводская
 * поставка и ручная правка.
 *
 * ЧТО ИЗМЕНИТСЯ - ВИДНО ДО ПРИМЕНЕНИЯ (SPEC 3.2-а). Набор ЗАМЕНЯЕТ привязки
 * целиком, а не дополняет их: узнать об этом после - слишком поздно, работа
 * получаса стирается одним нажатием. Поэтому окно показывает таблицу
 * «было → станет» по каждой затронутой привязке, а отмена не оставляет следов.
 */
export function Presets({
  gameId,
  presets,
  actions,
  current,
  gifts,
  open,
  onClose,
}: {
  gameId: string;
  presets: BindingPreset[];
  actions: BindableAction[];
  current: Binding[];
  gifts: GiftsPayload | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const apply = useApplyPreset(gameId);
  const toast = useToast();

  if (!open) {
    return null;
  }

  const preset = presets.find((item) => item.id === chosen) ?? null;

  const close = () => {
    setChosen(null);
    onClose();
  };

  return (
    <Modal
      wide
      open
      onClose={close}
      title="Готовые наборы привязок"
      actions={
        <>
          {preset === null && (
            <Reason id="presets-why" className="min-w-0 flex-1">
              Сначала выберите набор.
            </Reason>
          )}
          <Button variant="secondary" data-presets-cancel onClick={close}>
            Отмена
          </Button>
          <Button
            variant="primary"
            data-presets-apply
            loading={apply.isPending}
            disabled={preset === null}
            aria-describedby={preset === null ? 'presets-why' : undefined}
            onClick={() => {
              if (preset === null) return;

              /* Окно закрывается ПО НАЖАТИЮ, а не по ответу сервера: решение
                 уже принято - его подтвердили, увидев, что изменится. */
              const id = preset.id;
              close();

              apply.mutate(id, {
                onSuccess: () => {
                  toast.show({
                    tone: 'success',
                    title: 'Набор применён',
                    text: 'Прежние привязки заменены. Вернуть их можно сбросом к заводским.',
                  });
                },
              });
            }}
          >
            Применить набор
          </Button>
        </>
      }
    >
      <div data-presets className="flex w-full min-w-0 flex-col gap-3">
        {presets.length === 0 ? (
          <p className="text-sm">
            У этой игры готовых наборов нет. Привязки настраиваются по одной в панели слева.
          </p>
        ) : (
          <>
            <Choice
              label="Наборы"
              value={chosen ?? ''}
              onChange={setChosen}
              size="sm"
              options={presets.map((item) => ({
                value: item.id,
                label: label(item.labelKey),
                data: { 'data-preset': item.id },
              }))}
            />

            {preset === null ? (
              <p className="text-foreground/80 text-sm">
                Выберите набор — покажем, что именно изменится, до применения.
              </p>
            ) : (
              <Diff preset={preset} actions={actions} current={current} gifts={gifts} />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Таблица «было → станет» по каждой затронутой привязке. */
function Diff({
  preset,
  actions,
  current,
  gifts,
}: {
  preset: BindingPreset;
  actions: BindableAction[];
  current: Binding[];
  gifts: GiftsPayload | undefined;
}) {
  const next = preset.bindings;
  const keys = [...new Set([...current.map((item) => item.key), ...next.map((item) => item.key)])];

  const rows = keys.map((key) => ({
    key,
    was: current.find((item) => item.key === key),
    will: next.find((item) => item.key === key),
  }));

  const added = rows.filter((row) => row.was === undefined).length;
  const removed = rows.filter((row) => row.will === undefined).length;
  const changed = rows.length - added - removed;

  const name = (binding: Binding | undefined) => {
    if (binding === undefined) return '—';

    const action = actions.find((item) => item.id === binding.actionId);
    const condition = binding.conditions[0];

    return `${action === undefined ? binding.actionId : label(action.labelKey)}: ${triggerText(condition, gifts)}`;
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm">
        Набор заменит привязки целиком. Останется и перенастроится: {changed}, добавится: {added},
        удалится: {removed}.
      </p>

      <div className="border-foreground/10 max-h-72 overflow-y-auto rounded border">
        <table data-presets-diff className="w-full text-xs">
          <thead className="bg-muted/60 sticky top-0">
            <tr>
              <th scope="col" className="px-2 py-2 text-start font-medium">
                Сейчас
              </th>
              <th scope="col" className="px-2 py-2 text-start font-medium">
                Станет
              </th>
            </tr>
          </thead>
          <tbody className="divide-foreground/10 divide-y">
            {rows.map((row) => (
              <tr key={row.key} data-presets-diff-row={row.key}>
                <td className={`px-2 py-2 ${row.will === undefined ? 'text-destructive' : ''}`}>
                  {name(row.was)}
                </td>
                {/* Новое значение помечено ВЕСОМ, а не цветом: `text-primary`
                    на мелком тексте не проходит AA в обеих темах (решение
                    владельца 2026-09-26). */}
                <td className={`px-2 py-2 ${row.was === undefined ? 'font-semibold' : ''}`}>
                  {name(row.will)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-foreground/80 text-sm">
        Отменить можно сбросом к заводским — вернутся исходные привязки игры.
      </p>
    </div>
  );
}

function triggerText(
  condition: Binding['conditions'][number] | undefined,
  gifts: GiftsPayload | undefined,
): string {
  if (condition === undefined) return 'без условия';

  if (condition.event === 'gift') {
    if ('giftId' in condition) {
      const gift = gifts?.gifts.find((item) => item.id === condition.giftId);
      return gift === undefined ? condition.giftId : `${gift.name}, ${coins(gift.coins)}`;
    }

    return `любой подарок от ${coins(condition.minCoins)}`;
  }

  if (condition.event === 'like') {
    return `каждые ${condition.likeThreshold} лайков`;
  }

  if (condition.event === 'comment') {
    return `слово «${label(condition.keywordKey)}»`;
  }

  return (EVENT_LABELS[condition.event] ?? condition.event).toLowerCase();
}
